import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export type IconName =
  | 'caret-down'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'x'
  | 'trash'
  | 'search'
  | 'spinner'

export interface IconProps extends React.SVGAttributes<SVGElement> {
  name: IconName
  size?: number
  color?: string
  /** Whether to animate (spin) the icon */
  spin?: boolean
}

// ═══════════════════════════════════════════════════════════════════════════
// Icon Paths
// ═══════════════════════════════════════════════════════════════════════════

const iconPaths: Record<IconName, React.ReactNode> = {
  'caret-down': <path d="M7 10l5 5 5-5H7z" fill="currentColor" />,
  'chevron-down': (
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  'chevron-left': (
    <path
      d="M15 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  'chevron-right': (
    <path
      d="M9 18l6-6-6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  x: (
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  trash: (
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  search: (
    <path
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
  spinner: (
    <path
      d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-7.07l-2.83 2.83M9.76 14.24l-2.83 2.83m12.14 0l-2.83-2.83M9.76 9.76L6.93 6.93"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  ),
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled Icon component.
 *
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const Icon = React.memo(
  ({
    name,
    size = 24,
    color = 'currentColor',
    spin = false,
    style,
    className,
    ...props
  }: IconProps) => {
    const [css] = useStyletron()

    const iconClassName = css({
      display: 'inline-block',
      verticalAlign: 'middle',
      flexShrink: 0,
      color,
      ...(spin && {
        animationName: {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        } as unknown as string,
        animationDuration: '1s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'linear',
      }),
    })

    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={`${iconClassName} ${className || ''}`}
        style={style}
        aria-hidden="true"
        {...props}
      >
        {iconPaths[name]}
      </svg>
    )
  }
)

export default Icon
